import React, { useState, useEffect, memo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useTheme } from 'styled-components'

import { useBowtieLanguage, useBowtieStated } from '../../../../../hooks'
import { IReduxActions, IReduxReducers } from '../../../../../store'
import { IThreat, IConsequence, IZoomLevels, IBarrierTypeIcon, IBarrier } from '../../../../../types'
import { SpecialStatus } from '../../../../../types/enums'
import {
    barrierWidth,
    threatConsequenceWidth,
    initialThreat,
    initialConsequence,
    unfocusOpacity,
    threatConsequenceHeight,
    THREAT_COSNEQUENCE_GAP_X,
    BARRIER_GAP_X,
    THRAT_CONSEQUENCE_START_CURVE_GAP
} from '../../../../../utils/constants'
import { ThreatView, ConsequenceView } from '../../../../BowtieViews'
import { Barrier, ResumeBarrier } from './components'
import { TextContainer } from './styles'

interface Props {
    y: number
    type: 'threat' | 'consequence'
    threatConsequenceData: IThreat | IConsequence
    /** NOTE: data id is `thr-${barrierData.id}` for type threat or `cnq-${barrierData.id}` for type consequence */
    dataId: string
    event: ThreatView | ConsequenceView
    middleY: number
    x: number
    rootXIndex: number
    openClose: 'openAll' | 'closeAll' | 'none'
    zoomLevel: IZoomLevels
    barrierTypeIconMap: Map<number, IBarrierTypeIcon>
    bowtieMethodType: string
}

const ThreatAndConsequence: React.FC<Props> = ({
    type,
    y,
    x,
    threatConsequenceData,
    dataId,
    middleY,
    event: view,
    rootXIndex,
    openClose,
    zoomLevel,
    barrierTypeIconMap,
    bowtieMethodType
}) => {
    const theme = useTheme()
    const dispatch = useDispatch()
    const i18n = useBowtieLanguage()

    const event = useBowtieStated(view)

    const {
        consequencesExpandedBarriers,
        threatsExpandedBarriers,
        highlightDegradedBarriers,
        searchDataId
    } = useSelector((state: IReduxReducers) => ({
        ...state.bowtieExpandedBarriers,
        ...state.highlightDegradedBarriers,
        ...state.searchBarrier
    }))

    const openBrothersInside = (type === 'threat' ? threatsExpandedBarriers : consequencesExpandedBarriers).filter(el =>
        el.barrierId.startsWith(dataId + '|')
    )

    const [inactiveBarriersLength, setInactiveBarriersLength] = useState(0)
    const [showBarriers, setShowBarriers] = useState(false)

    const openBarriers = (): void => {
        const expandedBarriers = type === 'threat' ? threatsExpandedBarriers : consequencesExpandedBarriers

        if (expandedBarriers.length > 0) {
            dispatch<IReduxActions['bowtieExpandedBarriers']>({
                type: type === 'threat' ? 'SET_THREAT_EXPANDED_BARRIERS' : 'SET_CONSEQUENCE_EXPANDED_BARRIERS',
                payload: {
                    newExpandedBarriers: expandedBarriers.map(e => {
                        if (e.barrierId === dataId) {
                            if (type === 'threat') {
                                e.escalations[0].x =
                                    initialThreat -
                                    (threatConsequenceData.barriers?.length - 1 ?? 0) * (barrierWidth + BARRIER_GAP_X)
                            } else {
                                e.escalations[0].x =
                                    initialConsequence +
                                    (threatConsequenceData.barriers?.length - 1 ?? 0) * (barrierWidth + BARRIER_GAP_X)
                            }
                        }

                        return e
                    })
                }
            })
            setShowBarriers(true)
        }
    }

    const closeAllBarriers = (): void => {
        const expandedBarriers = type === 'threat' ? threatsExpandedBarriers : consequencesExpandedBarriers

        if (expandedBarriers.length > 0) {
            dispatch<IReduxActions['bowtieExpandedBarriers']>({
                type: type === 'threat' ? 'SET_THREAT_EXPANDED_BARRIERS' : 'SET_CONSEQUENCE_EXPANDED_BARRIERS',
                payload: {
                    newExpandedBarriers: expandedBarriers
                        .filter(e => !e.barrierId.startsWith(`${dataId}|`))
                        .map(e => {
                            if (e.barrierId === dataId) {
                                e.escalations[0].x = type === 'threat' ? initialThreat : initialConsequence
                            }

                            return e
                        })
                }
            })

            setShowBarriers(false)
        }
    }

    useEffect(() => {
        dispatch<IReduxActions['bowtieExpandedBarriers']>({
            type: type === 'threat' ? 'ADD_THREAT_EXPANDED_BARRIERS' : 'ADD_CONSEQUENCE_EXPANDED_BARRIERS',
            payload: {
                newExpandedBarriers: [
                    {
                        barrierId: dataId,
                        rootXIndex,
                        escalations: [{ escId: dataId, x, y }]
                    }
                ]
            }
        })

        closeAllBarriers()
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const expandedBarrier = type === 'threat' ? threatsExpandedBarriers : consequencesExpandedBarriers
        if (expandedBarrier.length > 0) {
            dispatch<IReduxActions['bowtieExpandedBarriers']>({
                type: type === 'threat' ? 'SET_THREAT_EXPANDED_BARRIERS' : 'SET_CONSEQUENCE_EXPANDED_BARRIERS',
                payload: {
                    newExpandedBarriers: expandedBarrier.map(e => {
                        if (e.barrierId === dataId) {
                            e.escalations = e.escalations.map(es => ({ ...es, y }))
                        }

                        return e
                    })
                }
            })
        }
    }, [y]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (view) {
            const inactiveBarriers = []

            view.barriersView.forEach(bar => {
                if (!bar.event || bar.event?.status === SpecialStatus.INACTIVE) {
                    inactiveBarriers.push(bar)
                }
            })

            setInactiveBarriersLength(inactiveBarriers.length)
        }
    }, [view.event]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        switch (openClose) {
            case 'openAll':
                setShowBarriers(false)
                setTimeout(() => openBarriers(), 25)
                break
            case 'closeAll':
                setShowBarriers(false)
                break
        }
    }, [openClose]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (searchDataId?.startsWith(`${dataId}|`) && !showBarriers) {
            openBarriers()
        } else if (searchDataId === dataId) {
            dispatch<IReduxActions['searchBarrier']>({
                type: 'SET_SEARCH_BARRIER_POSITION',
                payload: {
                    newSearchPosition: {
                        x:
                            type === 'threat'
                                ? showBarriers
                                    ? initialThreat -
                                      threatConsequenceWidth / 2 -
                                      20 -
                                      (threatConsequenceData.barriers ?? []).length * (barrierWidth + BARRIER_GAP_X)
                                    : initialThreat - threatConsequenceWidth / 2 - 20
                                : showBarriers
                                ? initialConsequence +
                                  threatConsequenceWidth / 2 +
                                  20 +
                                  (threatConsequenceData.barriers ?? []).length * (barrierWidth + BARRIER_GAP_X)
                                : initialConsequence + threatConsequenceWidth / 2 + 20,
                        y: y ?? 0
                    }
                }
            })
        }
    }, [searchDataId]) // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <>
            <path
                d={[
                    `M 0 ${middleY}`,
                    `L ${
                        type === 'threat'
                            ? initialThreat + THRAT_CONSEQUENCE_START_CURVE_GAP
                            : initialConsequence - THRAT_CONSEQUENCE_START_CURVE_GAP
                    } ${y}`,
                    `H ${type === 'threat' ? x - THREAT_COSNEQUENCE_GAP_X : x + THREAT_COSNEQUENCE_GAP_X}`
                ].join(' ')}
                strokeWidth={2}
                stroke={theme['light-borders']}
                strokeDasharray="5,5"
                // opacity={highlightDegradedBarriers ? (inactiveBarriersLength ? 1 : unfocusOpacity) : 1}
                opacity={highlightDegradedBarriers ? unfocusOpacity : 1}
                fill="none"
            />
            <g
                transform={`translate(${
                    type === 'threat'
                        ? x - threatConsequenceWidth - THREAT_COSNEQUENCE_GAP_X
                        : x + THREAT_COSNEQUENCE_GAP_X
                }, ${y - threatConsequenceHeight / 2 - (type === 'consequence' ? 2 : 0)})`}
                id={dataId}
                cursor="pointer"
                onClick={closeAllBarriers}
                // opacity={highlightDegradedBarriers ? (inactiveBarriersLength ? 1 : unfocusOpacity) : 1}
                opacity={highlightDegradedBarriers ? unfocusOpacity : 1}
            >
                {type === 'threat' ? (
                    <path
                        d="M 157.32 67.422 L 143.904 125.11 C 143.405 127.256 141.211 128.998 139.008 128.998 L 3.992 128.998 C 1.789 128.998 0 127.21 0 125.006 L 0 3.994 C 0 1.79 1.789 0.002 3.992 0.002 L 139.008 0.002 C 141.211 0.002 143.405 1.744 143.904 3.89 L 157.32 61.578 C 157.696 63.191 157.696 65.809 157.32 67.422 Z"
                        fill="#3866B1"
                    />
                ) : (
                    <>
                        {event?.value >= event?.threshold_value && (
                            <path
                                d="M 0.68 61.578 L 14.096 3.89 C 14.595 1.744 16.789 0.002 18.992 0.002 L 154.008 0.002 C 156.211 0.002 158 1.79 158 3.994 L 158 125.006 C 158 127.21 156.211 128.998 154.008 128.998 L 18.992 128.998 C 16.789 128.998 14.595 127.256 14.096 125.11 L 0.68 67.422 C 0.304 65.809 0.304 63.191 0.68 61.578 Z"
                                fill="true"
                                filter="url(#blur)"
                                stroke="#F00"
                                strokeWidth={2}
                                opacity={1}
                            />
                        )}
                        <path
                            d="M 0.68 61.578 L 14.096 3.89 C 14.595 1.744 16.789 0.002 18.992 0.002 L 154.008 0.002 C 156.211 0.002 158 1.79 158 3.994 L 158 125.006 C 158 127.21 156.211 128.998 154.008 128.998 L 18.992 128.998 C 16.789 128.998 14.595 127.256 14.096 125.11 L 0.68 67.422 C 0.304 65.809 0.304 63.191 0.68 61.578 Z"
                            fill="#8768A3"
                        />
                    </>
                )}
                {zoomLevel !== 'level0' && (
                    <>
                        <foreignObject
                            x={type === 'threat' ? 10 : 25}
                            y={8}
                            width={threatConsequenceWidth - 33}
                            height={threatConsequenceHeight - 33}
                            style={{ overflow: 'hidden' }}
                        >
                            <TextContainer>{threatConsequenceData?.name ?? i18n(type)}</TextContainer>
                        </foreignObject>

                        {bowtieMethodType === 'QUANTITATIVE' ? (
                            <>
                                <text
                                    x={type === 'threat' ? 10 : 25}
                                    y={threatConsequenceHeight - 28}
                                    textAnchor="start"
                                    dominantBaseline="hanging"
                                    fill="#fff"
                                    fontWeight={400}
                                    fontSize={11}
                                    className={
                                        type === 'threat'
                                            ? 'styledBowtieThreatInitialFreq'
                                            : 'styledBowtieConsequenceThresholdFreq'
                                    }
                                >
                                    {type === 'threat'
                                        ? `${i18n('Initial Freq.')}: ${Number(
                                              (threatConsequenceData as IThreat).initialFrequency
                                          )?.toExponential(4) ?? '---'}`
                                        : `${i18n('Threshold Freq.')}: ${Number(
                                              event?.threshold_value ??
                                                  (threatConsequenceData as IConsequence).thresholdFrequency
                                          )?.toExponential(4) ?? '---'}`}
                                </text>
                                <text
                                    x={type === 'threat' ? 10 : 25}
                                    y={threatConsequenceHeight - 15}
                                    textAnchor="start"
                                    dominantBaseline="hanging"
                                    fill="#fff"
                                    fontWeight={400}
                                    fontSize={11}
                                >
                                    {i18n('Final Freq.')}:{' '}
                                    {event?.value ? Number(event?.value).toExponential(4) : '---'}
                                </text>
                            </>
                        ) : null}
                    </>
                )}
            </g>
            {!showBarriers ? (
                <ResumeBarrier
                    x={type === 'threat' ? initialThreat : initialConsequence}
                    y={y - 25}
                    type={type}
                    barriersLength={
                        (
                            threatConsequenceData?.barriers.filter(
                                el => el.barrierType.barrierTypeSpecial !== 'UNMONITORED'
                            ) ?? []
                        ).length
                    }
                    view={view}
                    onClick={openBarriers}
                />
            ) : (
                view.barriersView?.map((barrierView, index) => {
                    const child = barrierView.barrier
                    const barrierX =
                        type === 'threat'
                            ? initialThreat - (barrierWidth + BARRIER_GAP_X) * index
                            : initialConsequence + (barrierWidth + BARRIER_GAP_X) * index
                    const barrierOpenCount =
                        openBrothersInside
                            .filter(el => el.rootXIndex > index)
                            .map(el => (el.escalations ?? []).length)
                            .reduce((a, b) => a + b, 0) ?? 0

                    return (
                        <Barrier
                            key={`${dataId}|bar-${child.id}`}
                            x={barrierX}
                            y={y + 10}
                            barrierData={child}
                            type={type}
                            event={barrierView}
                            uid={`${dataId}|bar-${child?.id}`}
                            dataId={`bar-${child?.id}`}
                            rootXIndex={index}
                            barrierBrothersY={barrierOpenCount}
                            zoomLevel={zoomLevel}
                            barrierTypeIconMap={barrierTypeIconMap}
                        />
                    )
                })
            )}
        </>
    )
}

export default memo(ThreatAndConsequence)
